"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import { useState } from "react";
import { Camera, Car, ClipboardCheck, type LucideIcon } from "lucide-react";
import {
  GoogleContactForm,
  type GoogleContactFormProps,
} from "@modes0/ui-components/client";
import { contactFormWidgetContent } from "@/content/business-details/business-content";

const GoogleReCAPTCHA = dynamic(() => import("react-google-recaptcha"), {
  ssr: false,
});

type GoogleContactFormWidgetProps = {
  content?: typeof contactFormWidgetContent;
  className?: string;
};

type WindowWithGoogleRecaptcha = Window & {
  grecaptcha?: {
    getResponse?: () => string;
    reset?: () => void;
  };
};

function isLocalBrowserHost() {
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function getGoogleRecaptchaResponse() {
  if (typeof window === "undefined") return null;
  const token = (
    window as WindowWithGoogleRecaptcha
  ).grecaptcha?.getResponse?.();
  return token || null;
}

function SecurityPattern() {
  const securityItems: {
    title: string;
    text: string;
    imageSrc: string;
    imageAlt: string;
    Icon: LucideIcon;
  }[] = [
    {
      title: "Systems",
      text: "CCTV, alarms, intercoms and access points reviewed together",
      imageSrc: "/images/services/1/sub-categories/1/c1-s1-p1.webp",
      imageAlt: "CCTV and electronic security support in Sydney",
      Icon: Camera,
    },
    {
      title: "Patrols",
      text: "Guarding, patrol duties and escalation paths scoped clearly",
      imageSrc: "/images/services/2/sub-categories/2/c2-s2-p1.webp",
      imageAlt: "Mobile patrol security support in Sydney",
      Icon: Car,
    },
    {
      title: "Planning",
      text: "Practical recommendations based on property layout and risk",
      imageSrc: "/images/services/2/sub-categories/3/c2-s3-p1.webp",
      imageAlt: "Security consultation and risk planning in Sydney",
      Icon: ClipboardCheck,
    },
  ];

  return (
    <div className="mt-8 hidden md:block">
      <div className="grid grid-cols-3 gap-3">
        {securityItems.map(({ imageAlt, imageSrc, title }) => (
          <div
            key={imageSrc}
            className="relative aspect-[4/3] overflow-hidden rounded-[8px] border border-[rgba(40,170,225,0.2)] bg-slate-100 shadow-sm"
          >
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              sizes="(min-width: 768px) 140px, 33vw"
              className="object-cover"
            />
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[rgba(21,23,30,0.84)] to-transparent px-3 pb-2 pt-8 text-xs font-extrabold uppercase text-white">
              {title}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-4 overflow-hidden rounded-[8px] border border-[rgba(40,170,225,0.2)] bg-white">
        <div className="grid gap-px bg-[rgba(40,170,225,0.16)]">
          {securityItems.map(({ Icon, text, title }) => (
            <div
              key={title}
              className="grid grid-cols-[42px_1fr] gap-4 bg-white px-5 py-4 text-left"
            >
              <span className="mt-1 flex h-8 w-8 items-center justify-center rounded-[8px] bg-[rgba(40,170,225,0.12)] text-[#28aae1]">
                <Icon size={19} strokeWidth={2.4} aria-hidden="true" />
              </span>
              <span>
                <span className="block text-sm font-extrabold uppercase text-[#28aae1]">
                  {title}
                </span>
                <span className="mt-1 block text-sm leading-6 text-[#344256]">
                  {text}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function GoogleContactFormWidget({
  content = contactFormWidgetContent,
  className,
}: GoogleContactFormWidgetProps) {
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [formResetNonce, setFormResetNonce] = useState(0);
  const isLocalHost = isLocalBrowserHost();
  const captchaSiteKey =
    isLocalHost && content.captcha.devSiteKey
      ? content.captcha.devSiteKey
      : content.captcha.siteKey;
  const formConfig = content.form as GoogleContactFormProps;
  const formApiUrl =
    isLocalHost && content.localApiUrl
      ? content.localApiUrl
      : formConfig.apiUrl;

  const resetCaptcha = () => {
    setCaptchaToken(null);
    (window as WindowWithGoogleRecaptcha).grecaptcha?.reset?.();
    setCaptchaKey((key) => key + 1);
  };

  const getCaptchaToken = () => captchaToken || getGoogleRecaptchaResponse();

  const handleSuccess: GoogleContactFormProps["onSuccess"] = (
    response,
    payload,
  ) => {
    formConfig.onSuccess?.(response, payload);
    setFormResetNonce((nonce) => nonce + 1);
  };

  return (
    <section
      id={content.section.id}
      className={className || "bg-white px-4 py-12"}
      aria-labelledby={content.section.titleId}
    >
      <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[0.85fr_1.15fr] md:items-start">
        <div className="pt-2">
          <p className="text-sm font-bold uppercase text-[#28aae1]">
            {content.section.eyebrow}
          </p>
          <h2
            id={content.section.titleId}
            className="mt-2 text-3xl font-bold text-[#15171e] md:text-4xl"
          >
            {content.section.title}
          </h2>
          <p className="mt-4 text-base leading-7 text-[#344256]">
            {content.section.description}
          </p>
          <SecurityPattern />
        </div>

        <div className="flex justify-center md:justify-end">
          <GoogleContactForm
            {...formConfig}
            apiUrl={formApiUrl}
            className="ozy-contact-form"
            initialValues={{
              ...(formConfig.initialValues ?? {}),
              __resetNonce: formResetNonce,
            }}
            onSuccess={handleSuccess}
            captchaElement={
              captchaSiteKey ? (
                <div className="ozy-recaptcha-field">
                  <div className="ozy-recaptcha-copy">
                    <span className="ozy-recaptcha-label">Security check</span>
                    <span className="ozy-recaptcha-note">
                      Tick the box before sending your enquiry.
                    </span>
                  </div>
                  <GoogleReCAPTCHA
                    key={captchaKey}
                    sitekey={captchaSiteKey}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                    onErrored={() => setCaptchaToken(null)}
                  />
                </div>
              ) : undefined
            }
            getCaptchaToken={captchaSiteKey ? getCaptchaToken : undefined}
            resetCaptcha={captchaSiteKey ? resetCaptcha : undefined}
          />
        </div>
      </div>
    </section>
  );
}
